import type { StringifyFilterValue } from '@app/src/hooks/queryState'
import {
  LEGACY_FILTER_PARAM,
  enumOptions,
  filterOn,
  mrtDeepLinkParam,
} from '@app/src/pages/ResourceCollection/MrtCollection/deepLink/mrtDeepLink'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { FilterOption } from '@app/src/pages/ResourceCollection/MrtCollection/mrtCollectionTypes'
import type { SpendFilterValue } from '@app/src/pages/ResourceCollection/MrtCollection/spendFilter/spendFilterValue'
import { ANY_PERIOD } from '@app/src/pages/ResourceCollection/MrtCollection/spendFilter/spendFilterValue'
import paths from '@app/src/wf-constants/paths'
import type { MRT_ColumnFiltersState } from 'material-react-table'
import qs from 'qs'
import type { IntlShape } from 'react-intl'
import { generatePath } from 'react-router'
import { ProviderTabs } from '../Providers/ProvidersScene'
import { formatTierLabel } from '@app/src/pages/ResourceCollection/MrtCollection/providerGroup/getProviderGroupColumns'

/**
 * Links that open the supplier table on a given set of filters, named by the column ids the table's own
 * registry defines. Keeping them here means a renamed column breaks a link at its definition rather than
 * at whichever dashboard card happens to point at it.
 *
 * Labels are built the same way the filter picker builds them, so a chip reads identically whether the
 * user chose it or followed a link.
 *
 * Each link states its filters twice, once per table: `tableFilters` for the MRT table and the pre-MRT
 * `filters` for the table the dispatcher still renders while newTablesWithMaterialReactTable is off.
 * Every legacy half below goes with that flag; the MRT halves are what stays.
 */

const providerTableLink = (
  view: ProviderTabs,
  columnFilters: MRT_ColumnFiltersState,
  legacyFilters: StringifyFilterValue[] = [],
): string =>
  `${generatePath(paths.providers, { view })}?${qs.stringify(
    {
      ...mrtDeepLinkParam(columnFilters),
      ...(legacyFilters.length ? { [LEGACY_FILTER_PARAM]: legacyFilters } : {}),
    },
    { encodeValuesOnly: true },
  )}`

const legacyIn = (name: string, values: (string | number)[]): StringifyFilterValue => ({
  name,
  value: values,
  operator: Operators.In,
})

/**
 * The whole tab, with nothing filtered. For the links that state a count: a filter left over from an
 * earlier visit would land the user on a shorter list than the number they just clicked.
 */
export const unfilteredProvidersLink = (view: ProviderTabs): string => providerTableLink(view, [])

export const providersByFinalRiskRatingLink = (
  finalRiskRatings: string[],
  formatMessage: IntlShape['formatMessage'],
): string =>
  providerTableLink(
    ProviderTabs.All,
    [
      filterOn(
        'finalRiskRating',
        enumOptions('schemas.provider.finalRiskRatingValues', finalRiskRatings, formatMessage),
      ),
    ],
    [legacyIn('finalRiskRating', finalRiskRatings)],
  )

/** The `name` column filters on supplier id and labels with the name, which is what lets a link name a
 * set of suppliers the table has no other way to express. */
export const providersByIdLink = (view: ProviderTabs, providers: { id: number; name?: string }[]): string =>
  providerTableLink(
    view,
    [
      filterOn(
        'name',
        providers.map(({ id, name }) => ({ value: String(id), label: name ?? String(id) })),
      ),
    ],
    [
      legacyIn(
        'id',
        providers.map(({ id }) => id),
      ),
    ],
  )

export type MissingSpendLink = {
  /** Absent means no spend in any period, which is what a warning counting across periods asks about. */
  periodName?: string
  /** Bounds the list the same way the count behind the link was bounded, or the two disagree. */
  createdBefore?: string
  /**
   * What the page holding the count was itself narrowed by, in both formats. A card embedded above
   * another table counts under that table's filters, so the link has to carry them or it opens a
   * longer list than the number that was clicked.
   */
  alsoFilteredBy?: { columnFilters: MRT_ColumnFiltersState; legacyFilters: StringifyFilterValue[] }
}

/**
 * `isQueryFilter` drops a filter without a truthy value, so the valueless "no spend at all" form has to
 * carry a placeholder through the URL for the legacy table to see the filter at all.
 */
const LEGACY_IS_EMPTY_PLACEHOLDER = 'true'
const LEGACY_SPEND_PERIOD_FIELD = 'spends.periodName'

const legacyMissingSpendFilters = ({ periodName, createdBefore }: MissingSpendLink): StringifyFilterValue[] => [
  periodName
    ? { name: LEGACY_SPEND_PERIOD_FIELD, value: periodName, operator: Operators.NotEqualTo }
    : { name: LEGACY_SPEND_PERIOD_FIELD, value: LEGACY_IS_EMPTY_PLACEHOLDER, operator: Operators.IsEmpty },
  ...(createdBefore ? [{ name: 'createdAt', value: createdBefore, operator: Operators.LowerThanOrEqual }] : []),
]

export const providersMissingSpendLink = (link: MissingSpendLink): string => {
  const { periodName, createdBefore, alsoFilteredBy } = link
  const spend: SpendFilterValue = { periodName: periodName ?? ANY_PERIOD, mode: 'notAdded' }

  return providerTableLink(
    ProviderTabs.All,
    [
      ...(alsoFilteredBy?.columnFilters ?? []),
      filterOn('spend', spend),
      ...(createdBefore ? [filterOn('createdAt', [null, createdBefore])] : []),
    ],
    [...(alsoFilteredBy?.legacyFilters ?? []), ...legacyMissingSpendFilters(link)],
  )
}

/** The categorisation filters the Sourcing dashboard offers, each of which the supplier table has a
 * column for. */
export type ProviderCategorisationFilters = {
  finalRiskRating?: string[]
  priority?: string[]
  providerApprovalStatus?: string[]
  supplierUsage?: string[]
  tier?: string[]
  owners?: FilterOption[]
}

/** The linking page's own filters, already in the format the legacy table reads. */
const toLegacyFilters = (groups: FilterGroup[]): StringifyFilterValue[] =>
  groups.flatMap(({ name, filters }) =>
    filters.flatMap(({ value, operator }) => (value === undefined ? [] : [{ name, value, operator }])),
  )

export const providersByCategorisationLink = (
  { finalRiskRating, priority, providerApprovalStatus, supplierUsage, tier, owners }: ProviderCategorisationFilters,
  formatMessage: IntlShape['formatMessage'],
  dashboardFilters: FilterGroup[] = [],
): string => {
  const enumFilter = (id: string, valuesIntlPrefix: string, values?: string[]) =>
    values?.length ? [filterOn(id, enumOptions(valuesIntlPrefix, values, formatMessage))] : []

  return providerTableLink(
    ProviderTabs.All,
    [
      ...enumFilter('finalRiskRating', 'schemas.provider.finalRiskRatingValues', finalRiskRating),
      ...enumFilter('priority', 'schemas.provider.priorityValues', priority),
      ...enumFilter('providerApprovalStatus', 'schemas.provider.providerApprovalStatusValues', providerApprovalStatus),
      ...enumFilter('supplierUsage', 'schemas.provider.supplierUsageValues', supplierUsage),
      ...(tier?.length
        ? [
            filterOn(
              'tier',
              tier.map(value => ({ value, label: formatTierLabel(formatMessage)(value) })),
            ),
          ]
        : []),
      ...(owners?.length ? [filterOn('ownerUsers', owners)] : []),
    ],
    toLegacyFilters(dashboardFilters),
  )
}

// Only linked to from the Insights tab, which exists only alongside the MRT table, so these carry no legacy filters.
export const providersByColumnValuesLink = (columnId: string, options: FilterOption[]): string =>
  providerTableLink(ProviderTabs.All, [filterOn(columnId, options)])

export const providersWithoutValueLink = (columnId: string): string =>
  providerTableLink(ProviderTabs.All, [{ id: columnId, value: { operator: 'empty' } }])

// Countries without a region: "region is empty" alone would also match suppliers with no country at all.
export const providersWithoutRegionLink = (): string =>
  providerTableLink(ProviderTabs.All, [
    { id: 'region', value: { operator: 'empty' } },
    { id: 'country', value: { operator: 'notEmpty' } },
  ])

/** Suppliers missing the value an Insights coverage row counts; spend has its own column filter for that. */
export const providersMissingValueLink = (columnId: string): string =>
  columnId === 'spend' ? providersMissingSpendLink({}) : providersWithoutValueLink(columnId)
